import { useMemo, useState } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { Tables, hashCode, MAX_TIMELINE_LENGTH } from "@repo/utils";
import { CELL_WIDTHS, MIN_FILLER_MONTHS } from "@utils";
import {
  useEstimateStructure,
  usePresetFieldNames,
  useEstimateEditorStore,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  useTableExpandHandler,
  TimelineContentSettings,
  TableTitle,
  TableContainer,
  VisibilityButton,
  EstimateTableSettingsProvider
} from "@tools";
import { ButtonSwitch } from "@components";
import { Grid, Collapse } from "@material-ui/core";
import { TableHeader } from "./components";
import { TimelineBilling, TimelineSections, TimelineTeamAllocation } from "./sections";
import useStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";
import classnames from "classnames";

export const TableTimeline = observer(({
  tableBreakdownNext,
  tableBreakdownPrev,
  isLastTable,
}) => {
  
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const {
    allowEdition,
    isSellerOrClient,
    isProposal,
    blockExpansions,
    forceExpansionArrows,
    showPdfCover,
    isPdfPrinterView,
  } = useEstimateEditorSettings();
  const { getFieldName } = usePresetFieldNames(isProposal);
  const classes = useStyle();
  
  const [headerRef, setHeaderRef] = useState(null);
  const [additionalFillerMonths, setAdditionalFillerMonths] = useState(0);
  
  const {
    settings,
    visibility,
    displayTotalTwoValues,
    displayAnyValue,
    totalDevelopmentTime,
    usedTimelineMode,
    workTypesHaveResources,
  } = structure;
  
  const {
    showTimeline,
    useMinMax,
    useTimelineSections,
    useTimelineAllocation,
    useTimelineBilling,
    maxValuesAsBuffer,
  } = settings;
  
  const {
    apply,
    hideTimeline,
    hideTimelineSections: vHideTimelineSections,
    hideTimelineAllocation: vHideTimelineAllocation,
    hideTimelineBilling: vHideTimelineBilling,
  } = visibility;
  
  const className = useVisibilityModeCellClassName(hideTimeline || !displayAnyValue);
  
  const useExpandCell = useMemo(() => (
    !isProposal || workTypesHaveResources || forceExpansionArrows
  ), [isProposal, workTypesHaveResources]);
  
  const timelineLength = useMemo(() => Math.min(
    Math.max(totalDevelopmentTime, MIN_FILLER_MONTHS) + additionalFillerMonths,
    MAX_TIMELINE_LENGTH
  ), [totalDevelopmentTime, additionalFillerMonths]);
  
  const timelineModeValues = useMemo(
    () => [
      {
        value: "min",
        label: getFieldName("pt-min"),
        className: classnames(
          "pt-min",
          `preset-timelineMode${usedTimelineMode === "min" ? "Active" : ""}`
        ),
      },
      {
        value: "max",
        label: getFieldName("pt-max"),
        className: classnames(
          "pt-max",
          `preset-timelineMode${usedTimelineMode === "max" ? "Active" : ""}`
        ),
      },
    ],
    [usedTimelineMode, getFieldName("pt-max"), getFieldName("pt-min")]
  );

  const tableContainerIdentifier = "timelineContainer";
  const tableIdentifier = "timelineHeader";
  
  const resizeHandler = (containerWidth) => {
    if(isPdfPrinterView)
      return
    const months = Math.max(totalDevelopmentTime, MIN_FILLER_MONTHS);
    // const minimalWidth = [...b.lastChild.children].slice(0,months).reduce((t,c) => t+c.offsetWidth, 0);
    const minimalWidth = months * CELL_WIDTHS.VALUE.SMALL;
    
    const availableWidth = containerWidth - CELL_WIDTHS.TITLE_SUMMARY - CELL_WIDTHS.DRAG_HANDLE /*- CELL_WIDTHS.VISIBILITY*/ - minimalWidth - 2;
    const freeSlots = Math.floor(availableWidth / CELL_WIDTHS.VALUE.SMALL);
    setAdditionalFillerMonths(freeSlots >= 0 ? freeSlots : 0);
  }

  const handleTimelineMode = (mode) => {
    if (blockExpansions) return;
    structure.historyManager.startGroup();
    settings.setTimelineMode(mode);
    structure.generateTimeline();
    structure.historyManager.stopGroup();
  };
  
  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: !blockExpansions,
    isOpened: showTimeline,
    onExpand: settings.setShowTimeline
  });
  
  return (
    <EstimateTableSettingsProvider
      useExpandCell={useExpandCell}
      timelineLength={timelineLength}
    >
      <Grid
        item container
        id={Tables.TIMELINE}
        justifyContent="center"
        alignItems="center"
        className={classnames(
          "preset-paperTimeline",
          classes.whiteContainer,
          tableBreakdownNext && classes.whiteContainerLast,
          tableBreakdownPrev && classes.whiteContainerFirst,
          isLastTable && !isPdfPrinterView && classes.paddingBottom,
        )}
      >
        <Grid
          item container 
          justifyContent="flex-end"
          className={classnames("vCon", showPdfCover && "pdf-mode", isPdfPrinterView && "printer-mode", classes.root)}
        >
          <TableTitle
            disabled={visibilityMode && (hideTimeline || !displayAnyValue)}
            expandable={!blockExpansions}
            presetClass="timeline"
            buttonPresetClass="preset-tableTimelineExpandButton"
            id={hashCode(Tables.TIMELINE)}
            title={getFieldName("pt-timeline")}
            showContent={showTimeline}
            onContentAction={handleOpen}
            className={classnames(isPdfPrinterView && "printer-mode")}
            action={<>
              { allowEdition && <TimelineContentSettings /> }
              { visibilityMode &&
                <VisibilityButton hidden={hideTimeline} onChange={visibility.setHideTimeline} />
              }
            </>}
          >
            {
              (allowEdition || useMinMax) &&
              <Grid
                item
                container
                alignItems="center"
                className="w-max my-1"
              >
                {
                  displayTotalTwoValues && !maxValuesAsBuffer &&
                  <ButtonSwitch
                    value={usedTimelineMode}
                    setValue={handleTimelineMode}
                    values={timelineModeValues}
                    containerClassName={classes.buttonSwitch}
                    size="small"
                  />
                }
              </Grid>
            }
          </TableTitle>
          <Collapse
            in={showTimeline}
            timeout="auto"
            mountOnEnter
            unmountOnExit
            className={classnames(showPdfCover && "pdf-mode", isPdfPrinterView && "printer-mode", classes.collapse, className)}
            { ...collapseProps }
          >
            <TableContainer
              tableHeaderRef={headerRef}
              tableContainerIdentifier={tableContainerIdentifier}
              useExpandCell
              onResize={resizeHandler}
              className={isPdfPrinterView ? "w-full mb-4" : classes.tableContainer}
            >
              <TableHeader
                ref={setHeaderRef}
                tableIdentifier={tableIdentifier}
              />
              {
                useTimelineSections && (!apply || visibilityMode || !vHideTimelineSections) &&
                <TimelineSections tableContainerIdentifier={tableContainerIdentifier} />
              }
              {
                useTimelineAllocation && (!apply || visibilityMode || !vHideTimelineAllocation) &&
                <TimelineTeamAllocation  />
              }
              {
                isSellerOrClient && useTimelineBilling && (!apply || visibilityMode || !vHideTimelineBilling) &&
                <TimelineBilling />
              }
            </TableContainer>
          </Collapse>
        </Grid>
      </Grid>
    </EstimateTableSettingsProvider>
  );
});

TableTimeline.propTypes = {
  tableBreakdownNext: bool,
  tableBreakdownPrev: bool,
  isLastTable: bool,
};
